import { fetchWithTimeout } from 't-lib-ui/connector';
import { Jurisdiction } from '../schema-copy/auth-schema';
import { getConfig } from './auth';

// Anonymous users must reach the API instance in their own data-residency region (EU data stays on EU
// servers). We learn the visitor's country from the auth Worker's GET /auth/geo endpoint (it runs at
// the Cloudflare edge, near the user), cache it, and derive the jurisdiction from it. Logged-in users
// are unaffected — their region comes from the account (see getApiHost in auth.ts).

// COPY of the EU-country list in aux-services/auth/lib/jurisdiction.ts. Kept in sync by hand, the same
// way schema-copy/ mirrors backend schema. "EU" means "this user's data must live in the EU". The set
// is EU 27 + EEA + UK + Switzerland + adjacent GDPR-equivalent micro-states / crown dependencies.
const EU_LIKE_COUNTRIES = new Set([
    // EU 27
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
    'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    // EEA non-EU (Iceland, Liechtenstein, Norway)
    'IS', 'LI', 'NO',
    // UK (UK GDPR) and Switzerland (FADP — EU adequacy)
    'GB', 'CH',
    // Adjacent GDPR-equivalent / adequacy regimes: UK crown dependencies, Gibraltar, micro-states
    'GG', 'JE', 'IM', 'GI', 'MC', 'SM', 'AD', 'VA',
]);

// Map a country code to a jurisdiction. Unknown/missing/Tor ('XX', 'T1', undefined) → 'US': the US
// stack is the global default and EU residency is opted into deliberately for known EU-like origins.
export function jurisdictionForCountry(country?: string): Jurisdiction {
    return country && EU_LIKE_COUNTRIES.has(country.toUpperCase()) ? 'EU' : 'US';
}

// Not `anon-` prefixed on purpose: logOut()'s localStorage.clear() removes the localStorage copy for
// free. The sessionStorage copy is explicitly preserved across logout (see logOut) so the geo decision
// survives even a logout, as long as the tab stays open.
const GEO_KEY = 'geo';

// Read the cached country: localStorage first, then sessionStorage. If it survived only in
// sessionStorage (e.g. right after a logout wiped localStorage), re-mirror it into localStorage.
function readStoredGeo(): string | undefined {
    try {
        const local = window.localStorage.getItem(GEO_KEY);
        if (local) return local;
        const session = window.sessionStorage.getItem(GEO_KEY);
        if (session) {
            window.localStorage.setItem(GEO_KEY, session);
            return session;
        }
    } catch { /* storage unavailable (private mode, etc.) — fall through to undefined */ }
    return undefined;
}

function storeGeo(geo: string): void {
    try {
        window.localStorage.setItem(GEO_KEY, geo);
        window.sessionStorage.setItem(GEO_KEY, geo);
    } catch { /* storage unavailable — a later boot will re-fetch */ }
}

// Resolve the visitor's country, fetching from the Worker only if not already cached. Called on boot
// before the first anonymous api request so it routes to the right region. On failure it returns 'XX'
// WITHOUT caching (→ jurisdiction 'US'), so the next boot retries instead of pinning US permanently.
export async function ensureGeo(): Promise<string> {
    const cached = readStoredGeo();
    if (cached) return cached;
    try {
        const r = await fetchWithTimeout(`${getConfig().authHost}/auth/geo`, { timeout: 5000 });
        if (!r.ok) return 'XX';
        const geo = (await r.json() as { geo?: string }).geo;
        if (!geo) return 'XX';
        storeGeo(geo);
        return geo;
    } catch {
        return 'XX';
    }
}

// The jurisdiction derived from the cached country, resolved synchronously for request routing. Defaults
// to 'US' when no geo is cached yet (ensureGeo() gates the anon boot so this is populated by first use).
export function getJurisdiction(): Jurisdiction {
    return jurisdictionForCountry(readStoredGeo());
}

// Whether the country is already cached, so callers can skip the boot loader for returning visitors.
export function hasStoredGeo(): boolean {
    return !!readStoredGeo();
}
